"use client";

import axios from "axios";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useState } from "react";

type UserRow = {
  id: number;
  name: string;
  email: string;
  role: string;
  is_active: number;
  created_at?: string;
};

export default function ProductAdminUsersPage() {
  const qc = useQueryClient();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [err, setErr] = useState<string | null>(null);

  const { data: users = [], isLoading } = useQuery({
    queryKey: ["product-admin", "users"],
    queryFn: async () => {
      const { data } = await api.get<{ users: UserRow[] }>("/api/product-admin/users");
      return data.users;
    },
  });

  const createUser = useMutation({
    mutationFn: async () => {
      await api.post("/api/product-admin/users", { name, email, password });
    },
    onSuccess: async () => {
      setName("");
      setEmail("");
      setPassword("");
      setErr(null);
      await qc.invalidateQueries({ queryKey: ["product-admin", "users"] });
    },
    onError: (e: unknown) => {
      let msg = "Could not create user.";
      if (axios.isAxiosError(e)) {
        const data = e.response?.data as { error?: string } | undefined;
        msg = data?.error ?? msg;
      }
      setErr(msg);
    },
  });

  const delUser = useMutation({
    mutationFn: async (id: number) => {
      await api.delete(`/api/product-admin/users/${id}`);
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ["product-admin", "users"] });
    },
  });

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Team</h1>
        <p className="text-sm text-muted-foreground">Product admin staff users (not tied to any company).</p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Add employee</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 sm:grid-cols-2">
          <div className="grid gap-2">
            <Label>Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Employee name" />
          </div>
          <div className="grid gap-2">
            <Label>Email</Label>
            <Input value={email} onChange={(e) => setEmail(e.target.value)} placeholder="employee@domain.com" />
          </div>
          <div className="grid gap-2 sm:col-span-2">
            <Label>Password</Label>
            <Input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Set login password"
            />
          </div>
          {err ? <p className="text-sm text-destructive sm:col-span-2">{err}</p> : null}
          <div className="sm:col-span-2">
            <Button
              type="button"
              disabled={createUser.isPending || !name || !email || !password}
              onClick={() => createUser.mutate()}
            >
              {createUser.isPending ? "Creating…" : "Create employee"}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Employees</CardTitle>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b text-left text-muted-foreground">
                    <th className="py-2 pr-3">ID</th>
                    <th className="py-2 pr-3">Name</th>
                    <th className="py-2 pr-3">Email</th>
                    <th className="py-2 pr-3">Role</th>
                    <th className="py-2 pr-3"></th>
                  </tr>
                </thead>
                <tbody>
                  {users.map((u) => (
                    <tr key={u.id} className="border-b last:border-0">
                      <td className="py-2 pr-3">{u.id}</td>
                      <td className="py-2 pr-3 font-medium">{u.name}</td>
                      <td className="py-2 pr-3">{u.email}</td>
                      <td className="py-2 pr-3">{u.role}</td>
                      <td className="py-2 pr-3 text-right">
                        {u.role === "product_admin" ? (
                          <span className="text-xs text-muted-foreground">owner</span>
                        ) : (
                          <Button
                            type="button"
                            variant="destructive"
                            size="sm"
                            disabled={delUser.isPending}
                            onClick={() => delUser.mutate(u.id)}
                          >
                            Delete
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

